import { Button, Icon } from "@deligoo/ui"
import clsx from "clsx"
import { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { shallowEqual, useDispatch } from "react-redux"

import { useAppSelector } from "@/App"
import { ContextMenu, openContextMenu } from "@/components/ContextMenu"
import { Panel } from "@/components/Panel"
import { Tabs } from "@/components/Tabs"
import { useOrdersContextMenuTrigger } from "@/modules/OrderContextMenu"
import { selectOrderById } from "@/store/orders"
import { setAboutPanel, setShowDriverDetails, setShowOrderDetails } from "@/store/ui"
import { Order } from "@/types"

import cls from "./Orders.module.scss"
import { OrderDetails } from "./partials/OrderDetails"
import { OrderHistory } from "./partials/OrderHistory"

type OrderPanelProps = {
  id: Order["id"]
  type: "pickup" | "delivery"
}

export const OrderPanel = ({ id, type }: OrderPanelProps) => {
  const order = useAppSelector(selectOrderById(id), shallowEqual)

  const [currentTab, setCurrentTab] = useState("details")

  const contextMenuRef = useOrdersContextMenuTrigger({ orderId: id })

  const dispatch = useDispatch()

  const { t } = useTranslation()

  const unselectedType = type === "pickup" ? "delivery" : "pickup"

  const handleTabChange = useCallback(() => {
    dispatch(setShowOrderDetails({ id, type: unselectedType }))
  }, [dispatch, id, unselectedType])

  if (!order) return null

  return (
    <Panel>
      <Panel.Header ref={contextMenuRef}>
        <Panel.Title>
          <h1 className="h400">
            {type === "pickup" ? order.pickup?.name : type === "delivery" ? order.delivery?.phone : `#${order.uid}`}
          </h1>

          {currentTab === "history" && <div className={clsx(cls.historyPanelSubtitle)}>#{order.uid}</div>}
        </Panel.Title>

        <Button
          color="primary"
          variation="text"
          size="small"
          className="mb-auto mt-1.5"
          onClick={() => dispatch(setShowOrderDetails(false))}
          iconProps={{ name: "close" }}
        />

        <div className={cls.tabs}>
          <Tabs currentTab={currentTab} setCurrentTab={setCurrentTab}>
            <Tabs.Tab label={t("details")} value="details" />
            <Tabs.Tab label={t("history")} value="history" />

            <div className="flex-center hide-mobile">
              <Button
                size="small"
                variation="text"
                className="mx-2"
                onClick={() => {
                  dispatch(setShowDriverDetails(order?.driver_id ? order.driver_id : false))
                  dispatch(setAboutPanel({ type: "order", id }))
                }}
              >
                <Icon name="crosshair" color="primary" />
              </Button>

              <ContextMenu.Button vertical={false} onClick={openContextMenu} />
            </div>
          </Tabs>
        </div>

        {currentTab === "details" && (
          <div className={cls.detailsMeta}>
            <Button
              iconProps={{ name: type }}
              color="primary"
              onClick={handleTabChange}
              disabled={!order[unselectedType]}
            >
              {t(`orders:${type}`)}
            </Button>

            <span className={cls.orderId}>#{order.uid}</span>
          </div>
        )}
      </Panel.Header>

      <Panel.Content>
        {currentTab === "details" && <OrderDetails order={order} type={type} />}
        {currentTab === "history" && <OrderHistory order={order} />}
      </Panel.Content>
    </Panel>
  )
}
